import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { List, IconButton, Collapse, Box, useTheme } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  Add as AddIcon,
  DeleteSweep as RecycleBinIcon,
  Folder as FolderIcon,
  LocalShipping as TrucksIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import type { TruckGroup } from "../../types/truckGroup";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface TruckGroupsMenuProps {
  truckGroups: TruckGroup[];
  userId?: string;
  loading: boolean;
  onNavigate?: () => void;
}

const headerStyles = {
  pl: 4,
  py: 1,
  color: "text.secondary",
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  fontWeight: 500,
  textTransform: "uppercase",
  fontSize: "0.75rem",
  letterSpacing: "0.5px",
} as const;

const GroupHeader: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...headerStyles,
        backgroundColor:
          theme.palette.mode === "dark"
            ? rawColor.white.transparent["05"]
            : rawColor.black.transparent["04"],
      }}
    >
      <FolderIcon sx={{ fontSize: "0.875rem" }} />
      {text}
    </Box>
  );
};

interface GroupItemProps {
  group: TruckGroup;
  isSelected: boolean;
  onNavigate?: () => void;
  "data-testid"?: string;
}

const GroupItem: React.FC<GroupItemProps> = ({
  group,
  isSelected,
  onNavigate,
  "data-testid": testId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <SidebarMenuItem
      to={`/trucks/groups/${group._id}`}
      selected={isSelected}
      icon={<FolderIcon fontSize="small" />}
      text={group.name}
      isSubmenu
      textProps={{ fontSize: "0.875rem" }}
      sx={{ pl: 7, py: 0.375, minHeight: "unset" }}
      onNavigate={onNavigate}
      data-testid={testId}
    />
  );
};

const TruckGroupsMenu: React.FC<TruckGroupsMenuProps> = ({
  truckGroups,
  userId,
  loading,
  onNavigate,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const manuallyCollapsedRef = useRef(false);
  const manuallyExpandedRef = useRef(false);

  // Check if current path is a trucks-related page
  const isTrucksPage = useMemo(() => {
    return (
      location.pathname === "/trucks" ||
      location.pathname.startsWith("/trucks/groups/") ||
      location.pathname === "/trucksByType" ||
      location.pathname === "/retiredTrucks"
    );
  }, [location.pathname]);

  // Initialize expanded state: collapsed by default, expanded if on trucks page
  const [truckGroupsExpanded, setTruckGroupsExpanded] = useState(() => isTrucksPage);

  // Auto-expand/collapse based on trucks page status and manual state
  useEffect(() => {
    if (isTrucksPage) {
      // On trucks page: respect manual state, but if no manual state, expand
      if (manuallyCollapsedRef.current) {
        // Keep collapsed if manually collapsed
        setTruckGroupsExpanded(false);
      } else {
        // Auto-expand on trucks pages (unless manually collapsed)
        setTruckGroupsExpanded(true);
        // Reset manual expanded state when on trucks page
        manuallyExpandedRef.current = false;
      }
    } else {
      // Not on trucks page: auto-collapse (reset manual state)
      setTruckGroupsExpanded(false);
      manuallyExpandedRef.current = false;
      manuallyCollapsedRef.current = false;
    }
  }, [isTrucksPage]);

  const toggleTruckGroups = useCallback(() => {
    setTruckGroupsExpanded((prev) => {
      const newValue = !prev;
      // Track manual state
      if (newValue) {
        // Manually expanded
        manuallyExpandedRef.current = true;
        manuallyCollapsedRef.current = false;
      } else {
        // Manually collapsed
        manuallyCollapsedRef.current = true;
        manuallyExpandedRef.current = false;
      }
      return newValue;
    });
  }, []);

  const { myTruckGroups, otherTruckGroups } = useMemo(
    () => ({
      myTruckGroups: truckGroups.filter(
        (group) => group.userId === userId || (userId && group.editorUserIds.includes(userId)),
      ),
      otherTruckGroups: truckGroups.filter(
        (group) => group.userId !== userId && !(userId && group.editorUserIds.includes(userId)),
      ),
    }),
    [truckGroups, userId],
  );

  const isGroupSelected = useCallback(
    (groupId: string) => location.pathname === `/trucks/groups/${groupId}`,
    [location.pathname],
  );

  const isTruckTypeSelected = useCallback(
    (type: string) =>
      location.pathname === "/trucksByType" && location.search.includes(`type=${type}`),
    [location.pathname, location.search],
  );

  const renderTruckTypeItem = useCallback(
    (type: "WINGBOX" | "LOSBAK" | "DUMP") => {
      const testId =
        type === "WINGBOX"
          ? TestId.SIDEBAR.TRUCK_TYPE_WINGBOX
          : type === "LOSBAK"
            ? TestId.SIDEBAR.TRUCK_TYPE_LOSBAK
            : TestId.SIDEBAR.TRUCK_TYPE_DUMP;

      return (
        <SidebarMenuItem
          key={type}
          to={`/trucksByType?type=${type}`}
          selected={isTruckTypeSelected(type)}
          text={t(type as TranslationKeys)}
          isSubmenu
          textProps={{ fontSize: "0.875rem" }}
          sx={{ pl: 7, py: 0.375, minHeight: "unset" }}
          onNavigate={onNavigate}
          data-testid={testId}
        />
      );
    },
    [isTruckTypeSelected, t, onNavigate],
  );

  const renderGroupSection = useCallback(
    (groups: TruckGroup[], headerText: TranslationKeys, renderCreateGroup = false) => (
      <>
        <GroupHeader text={t(headerText)} />
        {groups.map((group) => (
          <GroupItem
            key={group._id}
            group={group}
            isSelected={isGroupSelected(group._id)}
            onNavigate={onNavigate}
            data-testid={`${TestId.SIDEBAR.TRUCK_GROUP}-${group._id}`}
          />
        ))}
        {renderCreateGroup && (
          <SidebarMenuItem
            to="/trucks/groups/create"
            icon={<AddIcon fontSize="small" />}
            text={t("Create Group" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 7, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
          />
        )}
      </>
    ),
    [t, isGroupSelected, onNavigate],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <SidebarMenuItem
          to="/trucks"
          selected={location.pathname === "/trucks"}
          icon={<TrucksIcon />}
          text={t("Trucks" as TranslationKeys)}
          sx={{ flex: 1 }}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_TRUCKS}
        />
        <IconButton
          size="small"
          onClick={toggleTruckGroups}
          sx={{
            transform: truckGroupsExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
            position: "absolute",
            right: 4,
            zIndex: 1,
            color: rawColor.white.transparent["07"],
            "&:hover": {
              backgroundColor: rawColor.white.transparent["12"],
            },
          }}
        >
          <ArrowDownIcon />
        </IconButton>
      </Box>
      <Collapse in={truckGroupsExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding data-testid={TestId.SIDEBAR.TRUCK_GROUPS_MENU}>
          {!loading && (
            <>
              <GroupHeader text={t("Trucks by Type" as TranslationKeys)} />
              {renderTruckTypeItem("WINGBOX")}
              {renderTruckTypeItem("LOSBAK")}
              {renderTruckTypeItem("DUMP")}
              {renderGroupSection(myTruckGroups, "My groups" as TranslationKeys, true)}
              {otherTruckGroups.length > 0 &&
                renderGroupSection(otherTruckGroups, "Other groups" as TranslationKeys)}
              <SidebarMenuItem
                to="/retiredTrucks"
                selected={location.pathname === "/retiredTrucks"}
                icon={<RecycleBinIcon fontSize="small" />}
                text={t("Retired Trucks" as TranslationKeys)}
                isSubmenu
                textProps={{ fontSize: "0.875rem" }}
                sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
                onNavigate={onNavigate}
              />
            </>
          )}
        </List>
      </Collapse>
    </Box>
  );
};

export default TruckGroupsMenu;
